import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
  Container, Typography, Box, Paper, List, ListItem, ListItemText, Divider,
  CircularProgress, Chip, Tooltip, IconButton, Dialog, DialogTitle, DialogContent
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
// import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function MyFilesPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);
  const [userId, setUserId] = useState(null);
  const [textOpen, setTextOpen] = useState(false);
  const [textTitle, setTextTitle] = useState('');
  const [textLoading, setTextLoading] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [companyId, setCompanyId] = useState(null);
  const [teamIds, setTeamIds] = useState([]);
  const [isTeamAdmin, setIsTeamAdmin] = useState(false);
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [uploaderMap, setUploaderMap] = useState({});
  const navigate = useNavigate();

  const getAuthToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || '';
  }, []);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id || null;
      setUserId(uid);
      // Fetch company and team memberships + roles
      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('company_id')
          .eq('user_id', uid)
          .maybeSingle();
        setCompanyId(profile?.company_id || null);
      } catch {}
      try {
        const { data: memberships } = await supabase
          .from('team_members')
          .select('team_id, role_in_team')
          .eq('user_id', uid);
        const tids = (memberships || []).map(m => m.team_id);
        setTeamIds(tids);
        const admin = (memberships || []).some(m => (m.role_in_team || '').toLowerCase() === 'admin' || (m.role_in_team || '').toLowerCase() === 'owner');
        setIsTeamAdmin(admin);
      } catch {}
      try {
        const { data: cm } = await supabase
          .from('company_members')
          .select('*')
          .eq('user_id', uid)
          .maybeSingle();
        const role = (cm?.role_in_company || cm?.role || '').toLowerCase();
        if (role === 'admin' || role === 'owner') setIsCompanyAdmin(true);
      } catch {}
      const token = session?.access_token || '';
      const resp = await fetch(`${BACKEND_URL}/get_uploaded_files`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json();
      const list = Array.isArray(json.files) ? json.files : [];
      setFiles(list);
      // Build uploader map for display names
      const uploaderIds = Array.from(new Set((list || []).map(f => f.user_id || f.uploader_user_id).filter(id => id && id !== uid)));
      if (uploaderIds.length > 0) {
        try {
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('user_id, full_name, email')
            .in('user_id', uploaderIds);
          const map = (profiles || []).reduce((acc, p) => {
            acc[p.user_id] = { name: p.full_name || '', email: p.email || '' };
            return acc;
          }, {});
          setUploaderMap(map);
        } catch {}
      }
    } catch (e) {
      setError(e.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const isHttpUrl = (value) => {
    if (!value) return false;
    try { const u = new URL(value); return u.protocol === 'http:' || u.protocol === 'https:'; } catch { return false; }
  };

  const openOriginal = async (file) => {
    try {
      const fname = (file?.filename || '').toLowerCase();
      if (isHttpUrl(file?.source_url)) {
        if (fname.endsWith('.pdf') || file.source_url.toLowerCase().endsWith('.pdf')) {
          setPdfUrl(file.source_url);
          setPdfOpen(true);
        } else {
          window.open(file.source_url, '_blank', 'noopener,noreferrer');
        }
        return;
      }
      if (!file?.storage_path) return;
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || '';
      const encoded = encodeURIComponent(file.storage_path);
      const resp = await fetch(`${BACKEND_URL}/get_signed_url/${encoded}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json?.signedUrl) {
        if (fname.endsWith('.pdf') || json.signedUrl.toLowerCase().includes('.pdf')) {
          setPdfUrl(json.signedUrl);
          setPdfOpen(true);
        } else {
          window.open(json.signedUrl, '_blank', 'noopener,noreferrer');
        }
      }
    } catch (_) {}
  };

  const getTypeIcon = (file) => {
    if (isHttpUrl(file?.source_url)) return <LinkIcon fontSize="small" />;
    const name = (file?.filename || '').toLowerCase();
    if (name.endsWith('.pdf')) return <PictureAsPdfIcon fontSize="small" />;
    if (name.endsWith('.doc') || name.endsWith('.docx')) return <DescriptionIcon fontSize="small" />;
    return <InsertDriveFileIcon fontSize="small" />;
  };

  const openTextViewer = async (file) => {
    setTextTitle(file?.filename || 'File');
    setTextOpen(true);
    setTextLoading(true);
    setTextContent('');
    try {
      // Try by file_hash first, then fallback to storage_path
      let query = supabase.from('user_files').select('extracted_full_text').eq('file_hash', file.file_hash).maybeSingle();
      let { data, error: err1 } = await query;
      if (err1 || !data) {
        const res2 = await supabase.from('user_files').select('extracted_full_text').eq('storage_path', file.storage_path).maybeSingle();
        data = res2.data; // ignore err2; show error below if both fail
      }
      const content = (data && data.extracted_full_text) ? data.extracted_full_text : 'No extracted text available or you may not have permission to view it.';
      setTextContent(content);
    } catch (e) {
      setTextContent('Error loading text content.');
    } finally {
      setTextLoading(false);
    }
  };

  const groupFiles = () => {
    const my = [];
    const teamByUser = {};
    const companyByUser = {};
    const me = userId;
    files.forEach(f => {
      const uploaderId = f.user_id || f.uploader_user_id;
      if (uploaderId === me) { my.push(f); return; }
      if (isTeamAdmin && f.team_id && teamIds.includes(f.team_id)) {
        if (!teamByUser[uploaderId]) teamByUser[uploaderId] = [];
        teamByUser[uploaderId].push(f);
        return;
      }
      if (isCompanyAdmin && f.company_id && f.company_id === companyId && !f.team_id) {
        if (!companyByUser[uploaderId]) companyByUser[uploaderId] = [];
        companyByUser[uploaderId].push(f);
        return;
      }
    });
    return { my, teamByUser, companyByUser };
  };

  const Section = ({ title, items }) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>{title} <Chip size="small" label={items.length} /></Typography>
      <Paper variant="outlined">
        {items.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>No files.</Typography>
        ) : (
          <List dense>
            {items.map((f, i) => (
              <React.Fragment key={f.id || f.filename || i}>
                <ListItem
                  secondaryAction={(
                    <Tooltip title="Open original">
                      <IconButton edge="end" size="small" onClick={() => openOriginal(f)}>
                        {getTypeIcon(f)}
                      </IconButton>
                    </Tooltip>
                  )}
                >
                  <ListItemText
                    primary={
                      <Tooltip title="Click to view extracted text">
                        <Typography
                          variant="body2"
                          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => openTextViewer(f)}
                        >
                          {f.filename}
                        </Typography>
                      </Tooltip>
                    }
                    secondary={(
                      <>
                        <span>Status: {f.processing_status || 'completed'}</span>
                        {f.created_at && <span> • Uploaded: {new Date(f.created_at).toLocaleString()}</span>}
                      </>
                    )}
                  />
                </ListItem>
                {i < items.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );

  const { my, teamByUser, companyByUser } = groupFiles();

  return (
    <>
    <Container maxWidth="md" sx={{ py: 4 }}>
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Section title="My Files" items={my} />
          {isTeamAdmin && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Team Members</Typography>
            </Box>
          )}
          {isTeamAdmin && Object.entries(teamByUser).map(([uid, items]) => (
            <Section key={`team-${uid}`} title={(uploaderMap[uid]?.name || uploaderMap[uid]?.email || `${uid.substring(0,8)}…`)} items={items} />
          ))}
          {isCompanyAdmin && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Company Members</Typography>
            </Box>
          )}
          {isCompanyAdmin && Object.entries(companyByUser).map(([uid, items]) => (
            <Section key={`company-${uid}`} title={(uploaderMap[uid]?.name || uploaderMap[uid]?.email || `${uid.substring(0,8)}…`)} items={items} />
          ))}
        </>
      )}
    </Container>
    <Dialog open={textOpen} onClose={() => setTextOpen(false)} fullWidth maxWidth="md">
      <DialogTitle>{textTitle}</DialogTitle>
      <DialogContent dividers>
        {textLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13 }}>
            {textContent}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
    <Dialog open={pdfOpen} onClose={() => setPdfOpen(false)} fullWidth maxWidth="lg">
      <DialogTitle>File Preview</DialogTitle>
      <DialogContent dividers>
        {pdfUrl ? (
          <Box sx={{ height: '80vh' }}>
            <iframe title="file-preview" src={pdfUrl} style={{ width: '100%', height: '100%', border: 'none' }} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">No preview available.</Typography>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}


